import { h, onUnmounted, Ref } from 'vue';
import { checkboxField, inputField } from '@/ui/components/form';
import { Task, useValidation } from '@/ui/concerns';
import { User, validate } from '@/entities/user';
import { apiErrorMessage } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { dropdownField } from '@/ui/components/form/DropdownField';
import { SelectOptions } from '@/ui/types';
import { USER_ROLE_ADMINISTRATOR } from '@/services/api/resources/user';
import { mergedRoleOptions } from '@/ui/pages/user/options';
import { hasFeature } from '@/ui/auth/auth';
import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
import { StarredAdItem } from '@/ui/components/upgrade/StarredAdItem';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';

export function useUserCreateForm(record: Ref<User>, task: Task) {
  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation(validate, record, task.state);

  const handleSubmit = () => {
    showValidationErrors.value = true;
    if (hasNoValidationErrors.value) {
      task.run();
    }
  };

  // When user is created via modal
  // we need to reset the validation errors
  const onUserCreated = () => {
    showValidationErrors.value = false;
  };

  const detachListeners = () => {
    EventBus.$off(Events.userCreated, onUserCreated);
  };

  EventBus.$on(Events.userCreated, onUserCreated);

  onUnmounted(detachListeners);

  const roles = (roleOptions: SelectOptions) =>
    dropdownField(
      {
        name: 'role',
        label: t('common.role'),
        //administrator by default
        value: record.value.role || USER_ROLE_ADMINISTRATOR,
        placeholder: t('form.select-role'),
        required: false,
        options: mergedRoleOptions(roleOptions),
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.role = value;
      },
    );

  const upgradeForRoles = () => h('div', [
    h(StarredAdItem, { props: { isStarred: true } }, [
      h('label', { class: 'label opacity-50' }, [t('common.role')]),
    ]),
    h(UpgradeAd, {
      class: 'upgrade-ad--create-user-roles',
      props: {
        message: t('upgrade-ad.user-create-role'),
        isVertical: true,
        withDropdown: true,
        placeholder: t('common.administrator'),
      },
    }),
  ]);

  const formFields = (
    roleOptions: SelectOptions,
  ) => [
    inputField(
      {
        name: 'name',
        label: t('common.name'),
        placeholder: t('user.form.enter-name'),
        value: record.value.name,
        required: true,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.name = value;
      },
    ),

    inputField(
      {
        name: 'email',
        label: t('common.email'),
        placeholder: t('user.form.enter-email'),
        value: record.value.email,
        required: true,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.email = value;
      },
    ),

    hasFeature('roles')
      ? roles(roleOptions)
      : upgradeForRoles(),

    checkboxField(
      {
        name: 'invite',
        label: t('user.send-invitation'),
        value: record.value.invite,
        required: false,
        disabled: task.state.value.pending,
        errors: validationErrors.value,
      },
      value => {
        record.value.invite = value;
      },
    ),

    record.value.invite === false
      ? inputField({
        name: 'password',
        type: 'password',
        label: t('auth.password'),
        placeholder: '',
        value: record.value.password ?? '',
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      }, value => {
        record.value.password = value;
      }) : null,

    apiErrorMessage(task.state.value),
  ];

  return {
    formFields,
    handleSubmit,
  };
}
